import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRepairContext } from "@/context/RepairContext";
import { Loader2, TrendingUp, DollarSign, Calendar } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  subMonths,
} from "date-fns";
import { RepairItem } from "@/types";

// Helper function to calculate the final price of a repair including modifications
const calculateFinalPrice = (repair: RepairItem): number => {
  // Start with the base repair price
  let finalPrice = repair.repairPrice || 0;

  // Add all price modifications if they exist
  if (repair.priceModifications && repair.priceModifications.length > 0) {
    const modificationsTotal = repair.priceModifications.reduce(
      (sum, mod) => sum + (mod.amount || 0),
      0
    );
    finalPrice += modificationsTotal;
  }

  return finalPrice;
};

// Helper function to calculate the total cost of a repair including cost modifications
const calculateTotalCost = (repair: RepairItem): number => {
  // Start with the base repair cost
  let totalCost = repair.repairCost || 0;

  // Add all cost modifications if they exist
  if (repair.costModifications && repair.costModifications.length > 0) {
    const modificationsTotal = repair.costModifications.reduce(
      (sum, mod) => sum + (mod.amount || 0),
      0
    );
    totalCost += modificationsTotal;
  }

  return totalCost;
};

interface IncomeData {
  date: string;
  amount: number;
  cost: number;
  profit: number;
  formattedDate: string;
}

const IncomeWidget: React.FC = () => {
  const { t } = useTranslation();
  const { repairs } = useRepairContext();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IncomeData[]>([]);
  const [stats, setStats] = useState({
    totalIncome: 0,
    monthlyIncome: 0,
    previousMonthIncome: 0,
    percentChange: 0,
    totalCost: 0,
    monthlyCost: 0,
    totalProfit: 0,
    monthlyProfit: 0,
  });

  useEffect(() => {
    if (repairs) {
      // Get paid repairs - consider ALL repairs that are marked as paid
      // This ensures we capture all paid amounts regardless of repair status
      const paidRepairs = repairs.filter((r) => r.paymentStatus === "paid");

      // Calculate total income and cost using the final price and cost (including modifications)
      const totalIncome = paidRepairs.reduce((sum, repair) => {
        return sum + calculateFinalPrice(repair);
      }, 0);

      const totalCost = paidRepairs.reduce((sum, repair) => {
        return sum + calculateTotalCost(repair);
      }, 0);

      const totalProfit = totalIncome - totalCost;

      // Calculate current month income
      const now = new Date();
      const currentMonthStart = startOfMonth(now);
      const currentMonthEnd = endOfMonth(now);

      // For current month, use completedAt date for completed repairs with paid status
      const currentMonthRepairs = paidRepairs.filter(
        (r) =>
          r.completedAt &&
          new Date(r.completedAt) >= currentMonthStart &&
          new Date(r.completedAt) <= currentMonthEnd
      );

      const monthlyIncome = currentMonthRepairs.reduce((sum, repair) => {
        return sum + calculateFinalPrice(repair);
      }, 0);

      const monthlyCost = currentMonthRepairs.reduce((sum, repair) => {
        return sum + calculateTotalCost(repair);
      }, 0);

      const monthlyProfit = monthlyIncome - monthlyCost;

      // Calculate previous month income
      const previousMonthStart = startOfMonth(subMonths(now, 1));
      const previousMonthEnd = endOfMonth(subMonths(now, 1));

      // For previous month, use completedAt date for completed repairs with paid status
      const previousMonthRepairs = paidRepairs.filter(
        (r) =>
          r.completedAt &&
          new Date(r.completedAt) >= previousMonthStart &&
          new Date(r.completedAt) <= previousMonthEnd
      );

      const previousMonthIncome = previousMonthRepairs.reduce((sum, repair) => {
        return sum + calculateFinalPrice(repair);
      }, 0);

      // Calculate percent change
      const percentChange =
        previousMonthIncome === 0
          ? 100
          : ((monthlyIncome - previousMonthIncome) / previousMonthIncome) * 100;

      setStats({
        totalIncome,
        monthlyIncome,
        previousMonthIncome,
        percentChange,
        totalCost,
        monthlyCost,
        totalProfit,
        monthlyProfit,
      });

      // Prepare chart data for current month
      const daysInMonth = eachDayOfInterval({
        start: currentMonthStart,
        end: currentMonthEnd,
      });

      const dailyData = daysInMonth.map((day) => {
        const dayRepairs = paidRepairs.filter(
          (r) => r.completedAt && isSameDay(new Date(r.completedAt), day)
        );

        const amount = dayRepairs.reduce((sum, repair) => {
          return sum + calculateFinalPrice(repair);
        }, 0);

        const cost = dayRepairs.reduce((sum, repair) => {
          return sum + calculateTotalCost(repair);
        }, 0);

        const profit = amount - cost;

        return {
          date: format(day, "yyyy-MM-dd"),
          formattedDate: format(day, "dd"),
          amount,
          cost,
          profit,
        };
      });

      // Group by week to reduce data points
      const weeklyData: IncomeData[] = [];
      for (let i = 0; i < dailyData.length; i += 7) {
        const weekData = dailyData.slice(i, i + 7);
        const weekAmount = weekData.reduce((sum, day) => sum + day.amount, 0);
        const weekCost = weekData.reduce((sum, day) => sum + day.cost, 0);
        const weekProfit = weekData.reduce((sum, day) => sum + day.profit, 0);
        const startDate = weekData[0].formattedDate;
        const endDate = weekData[weekData.length - 1].formattedDate;

        weeklyData.push({
          date: weekData[0].date,
          formattedDate: `${startDate}-${endDate}`,
          amount: weekAmount,
          cost: weekCost,
          profit: weekProfit,
        });
      }

      setData(weeklyData);
      setLoading(false);
    }
  }, [repairs]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("ar-TN", {
      style: "currency",
      currency: "TND",
      maximumFractionDigits: 3,
    }).format(value);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4" dir="ltr">
        {" "}
        {/* Force LTR direction for grid layout */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
          <div className="flex items-center">
            <Calendar className="h-4 w-4 mr-1 text-blue-500" />
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t("dashboard.monthlyIncome")}
            </div>
          </div>
          <div className="text-2xl font-bold">
            {formatCurrency(stats.monthlyIncome)}
          </div>
          <div className="flex items-center text-xs mt-1">
            {stats.percentChange > 0 ? (
              <TrendingUp className="h-3 w-3 mr-1 text-green-500" />
            ) : (
              <TrendingUp className="h-3 w-3 mr-1 text-red-500 transform rotate-180" />
            )}
            <span
              className={
                stats.percentChange > 0 ? "text-green-500" : "text-red-500"
              }
            >
              {stats.percentChange.toFixed(1)}%{" "}
              {t(
                stats.percentChange > 0
                  ? "dashboard.increase"
                  : "dashboard.decrease"
              )}
            </span>
          </div>
        </div>
        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg">
          <div className="flex items-center">
            <DollarSign className="h-4 w-4 mr-1 text-emerald-500" />
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t("dashboard.totalIncome")}
            </div>
          </div>
          <div className="text-2xl font-bold">
            {formatCurrency(stats.totalIncome)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t("dashboard.allTime")}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4" dir="ltr">
        <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
          <div className="flex items-center">
            <DollarSign className="h-4 w-4 mr-1 text-red-500" />
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t("dashboard.monthlyCost")}
            </div>
          </div>
          <div className="text-2xl font-bold">
            {formatCurrency(stats.monthlyCost)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t("dashboard.allTime")}
          </div>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
          <div className="flex items-center">
            <DollarSign className="h-4 w-4 mr-1 text-purple-500" />
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t("dashboard.monthlyEarnings")}
            </div>
          </div>
          <div className="text-2xl font-bold">
            {formatCurrency(stats.monthlyProfit)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t("dashboard.allTime")}
          </div>
        </div>
      </div>

      <div className="h-[180px] mt-4" dir="ltr">
        {" "}
        {/* Force LTR direction for chart */}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{
              top: 5,
              right: 5,
              left: 0,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="formattedDate" tick={{ fontSize: 10 }} />
            <YAxis
              tickFormatter={(value) => `${value} TND`}
              tick={{ fontSize: 10 }}
            />
            <Tooltip
              formatter={(value: number, name: string) => {
                const label =
                  name === "amount"
                    ? t("dashboard.income")
                    : name === "cost"
                    ? t("dashboard.cost")
                    : t("dashboard.earnings");
                return [formatCurrency(value), label];
              }}
              contentStyle={{
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                borderRadius: "4px",
                border: "1px solid #e2e8f0",
                padding: "8px",
              }}
            />
            <Bar
              dataKey="amount"
              name="amount"
              fill="#3b82f6"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="cost"
              name="cost"
              fill="#ef4444"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="profit"
              name="profit"
              fill="#8b5cf6"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default IncomeWidget;
